import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { FormattedMessage as FM } from "react-intl";
import { addDays, addMonths } from "date-fns";

import dates from "componentsv2/tools/dates";

import { ChevronDownIcon, EllipsisHorizontalIcon } from "components/Icons";
import useLangStore from "store/langStore";
import { useContext, setTimeData } from "../store/useContext";
import { useViewModeStore } from "../store/storeView";
import Selection from "componentsv2/form/Selection";
import { useCompactHeader } from "../hooks/useCompactHeader";

const VIEWS = ["day", "week", "month"];
const STYLE_MENUITEM =
  "block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none";

function HeaderCalendar(props) {
  const { handleDay, filter } = props;
  const { context, updateContext } = useContext();
  const { setViewMode, viewMode } = useViewModeStore();
  const { currentDate } = context;
  const type = "room";
  const today = dates.startOfToday();
  const { locale } = useLangStore();
  const isCompact = useCompactHeader(".agenda-container", [viewMode]);

  async function handleView(view) {
    setViewMode(view);
    updateContext({ events: new Map(), availableTime: [] });
    await setTimeData();
  }

  async function onChangeDate(direction) {
    let currentDate = context.currentDate;
    if (direction === "today") {
      currentDate = today;
    } else {
      const diff = direction === "previous" ? -1 : 1;

      const addWeeks = () => {
        return addDays(currentDate, diff * 7);
      };

      const func =
        viewMode === "day"
          ? addDays
          : viewMode === "week"
            ? addWeeks
            : addMonths;

      currentDate = func(currentDate, diff);
    }

    updateContext({ currentDate });
    await setTimeData();
  }

  async function onChangeSelection(_, value) {
    if (type === "professional") {
      updateContext({
        currentOwner: value,
        events: new Map(),
        availableTime: [],
      });
    }

    if (type === "room") {
      updateContext({
        currentRoom: value,
        events: new Map(),
        availableTime: [],
      });
    }
    await setTimeData();
  }

  const roomFilter = {
    type: "many2one",
    model: "appointment_scheduling.room",
    readOnly: false,
    data: {
      value: context.currentRoom, // El objeto completo de la sala
    },
    options: context.rooms || [],
    onChange: onChangeSelection,
  };

  return (
    <div
      className={`sticky top-0 -mt-2 z-10 bg-white border-b border-gray-200 transition-all duration-300 ease-in-out ${
        isCompact ?? "py-1 shadow-sm"
      }`}
    >
      <div
        className={`transition-all duration-300 ease-in-out transform ${
          isCompact ? "scale-95 opacity-90" : "scale-100 opacity-100"
        }`}
      >
        {isCompact ? (
          <div className="flex justify-around items-center px-4 py-2">
            <h1 className="text-base font-semibold text-gray-900">
              <time dateTime={currentDate.toISOString()}>
                {currentDate.toLocaleDateString(locale, {
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </h1>
            <Selection name="room" {...roomFilter} />
          </div>
        ) : (
          <header className="flex flex-wrap items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none sticky top-0 z-10">
            {viewMode !== "day" ? (
              <h1 className="text-base font-semibold text-gray-900">
                <time dateTime={currentDate.toISOString()}>
                  {currentDate.toLocaleDateString(locale, {
                    month: "long",
                    year: "numeric",
                  })}
                </time>
              </h1>
            ) : (
              <div>
                <h1 className="text-base font-semibold text-gray-900">
                  <time
                    dateTime={currentDate.toISOString()}
                    className="sm:hidden"
                  >
                    {currentDate.toLocaleDateString("es-ES", {
                      month: "short",
                      day: "2-digit",
                      year: "numeric",
                    })}
                  </time>
                  <time
                    dateTime={currentDate.toISOString()}
                    className="hidden sm:inline"
                  >
                    {currentDate.toLocaleDateString("es-ES", {
                      month: "long",
                      day: "2-digit",
                      year: "numeric",
                    })}
                  </time>
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {currentDate.toLocaleDateString("es-ES", {
                    weekday: "long",
                  })}
                </p>
              </div>
            )}
            {viewMode !== "day" && (
              <div className="hidden lg:flex flex-row relative min-w-2xl m-auto items-center space-x-2">
                <span className="text-gray-900 font-semibold">
                  <FM
                    id={
                      type === "professional"
                        ? "appointment_scheduling.professional"
                        : "appointment_scheduling.room"
                    }
                  />
                </span>
                <Selection name="room" {...roomFilter} />
              </div>
            )}
            <div className="flex items-center">
              <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
                <button
                  type="button"
                  name="previous"
                  onClick={() => onChangeDate("previous")}
                  className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
                >
                  <span className="sr-only">
                    <FM id={`previous_${viewMode}`} />
                  </span>
                  <ChevronDownIcon
                    className="size-5 fill-current rotate-90"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  name="today"
                  onClick={() => onChangeDate("today")}
                  className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
                >
                  <FM id={"today"} />
                </button>
                <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
                <button
                  type="button"
                  name="next"
                  onClick={() => onChangeDate("next")}
                  className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
                >
                  <span className="sr-only">
                    <FM id={`next_${viewMode}`} />
                  </span>
                  <ChevronDownIcon
                    className="size-5 fill-current -rotate-90"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <div className="hidden md:ml-4 md:flex md:items-center">
                <Menu as="div" className="relative">
                  <MenuButton
                    type="button"
                    className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    <FM id={`${viewMode}.view`} />
                    <ChevronDownIcon
                      className="-mr-1 size-5 text-gray-400 fill-current"
                      aria-hidden="true"
                    />
                  </MenuButton>

                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <div className="py-1">
                      {VIEWS.map((view_) => (
                        <MenuItem key={view_}>
                          <span
                            className={STYLE_MENUITEM}
                            onClick={() => handleView(view_)}
                          >
                            <FM id={`${view_}.view`} />
                          </span>
                        </MenuItem>
                      ))}
                    </div>
                  </MenuItems>
                </Menu>
                {viewMode !== "day" && (
                  <>
                    <div className="ml-6 h-6 w-px bg-gray-300" />
                    <button
                      type="button"
                      onClick={(e) => handleDay(e)}
                      className="ml-6 rounded-md bg-sky-950 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline-offset-2 focus-visible:outline-slate-950"
                    >
                      <FM id={"schedule_appointment"} />
                    </button>
                  </>
                )}
              </div>
              <Menu as="div" className="relative ml-6 md:hidden">
                <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Open menu</span>
                  <EllipsisHorizontalIcon
                    className="size-5 fill-current"
                    aria-hidden="true"
                  />
                </MenuButton>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    <MenuItem id="add_event">
                      <span
                        className={STYLE_MENUITEM}
                        onClick={() => handleDay()}
                      >
                        Agendar cita
                      </span>
                    </MenuItem>
                  </div>
                  <div className="py-1">
                    <MenuItem id="today">
                      <span
                        className={STYLE_MENUITEM}
                        onClick={() => onChangeDate("today")}
                      >
                        Ver hoy
                      </span>
                    </MenuItem>
                  </div>
                  <div className="py-1">
                    {VIEWS.map((view_) => (
                      <MenuItem key={view_}>
                        <span
                          className={STYLE_MENUITEM}
                          onClick={() => handleView(view_)}
                        >
                          <FM id={`${view_}.view`} />
                        </span>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Menu>
            </div>
            <div className="isolate lg:hidden flex-row relative w-full m-auto items-center space-x-2">
              <span className="text-gray-900 font-semibold">
                <FM
                  id={
                    type === "professional"
                      ? "appointment_scheduling.professional"
                      : "appointment_scheduling.room"
                  }
                />
              </span>
              <Selection name="room" {...roomFilter} />
            </div>
          </header>
        )}
      </div>
    </div>
  );
}

export default HeaderCalendar;
